import React from "react"
import { graphql } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import styled from "styled-components"

import Layout from "../components/layout-v2"
import Seo from "../components/seo"
import StandardPageBuilder from "../components/standard-page-builder"

const Page = ({ data: { page, bannerImg } }) => {
  const layouts = page.PageBuilder.standardPageBuilder

  return (
    <Layout>
      <Seo
        title={page.SEOmeta.metaTitle}
        description={page.SEOmeta.metaDescription}
        keywords={page.SEOmeta.metaKeywords}
        ogTitle={page.SEOmeta.ogTitle}
        ogDescription={page.SEOmeta.ogDescription}
        ogImage={page.SEOmeta.ogImage.localFile.childImageSharp.fluid}
        twitterTitle={page.SEOmeta.twitterTitle}
        twitterDescription={page.SEOmeta.twitterDescription}
        twitterImage={page.SEOmeta.twitterImage.localFile.childImageSharp.fluid}
      />
      <article style={{ padding: "0 20px", overflow: "hidden" }}>
        <TitleSection>
          <div class="header-border">
            <h1>{page.title}</h1>
            <p>{page.PageBuilderSubtitle.pbPageSubtitle}</p>
          </div>
          <div class="hero-banner banner-2">
            <div
              data-sal="fade"
              data-sal-duration="1000"
              data-sal-easing="ease"
            >
              <GatsbyImage
                className={"hero-bg hero-bg-2"}
                image={bannerImg.childImageSharp.gatsbyImageData}
                alt={`Hahn.Agency ${page.title} Banner Background`}
              />
            </div>
          </div>
        </TitleSection>
        <div>
          {layouts.map((layout, index) => (
            <StandardPageBuilder key={index} layoutData={layout} />
          ))}
        </div>
      </article>
    </Layout>
  )
}

const TitleSection = styled.header`
  position: relative;
  max-width: 1320px;
  width: 100%;
  padding: 40px;
  margin: 0 auto;
  margin-top: 20px;
  margin-bottom: 60px;
  background-color: #000;
  .header-border {
    border: 10px solid #fff;
    padding: 120px;
    position: relative;
    z-index: 10;
  }
  .hero-banner {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    div {
      height: 100%;
    }
    .gatsby-image-wrapper {
      height: 100%;
    }
    img {
      width: 100%;
      height: 100%;
    }
    .hero-bg {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;
      opacity: 0;
      visibility: hidden;
    }
    &.banner-1 {
      .hero-bg-1 {
        opacity: 1;
        visibility: visible;
      }
    }
    &.banner-2 {
      .hero-bg-2 {
        opacity: 1;
        visibility: visible;
      }
    }
  }
  h1 {
    font-size: 100px;
    color: #fff;
    margin-top: 0;
    margin-bottom: 20px;
    position: relative;
    z-index: 2;
    text-align: center;
    &:hover {
      cursor: default;
    }
  }
  p {
    font-family: "Work Sans";
    color: #fff;
    font-size: 44px;
    font-weight: 400;
    line-height: 1.3;
    margin-bottom: 0;
    position: relative;
    z-index: 2;
    text-align: center;
    &:hover {
      cursor: default;
    }
  }
  @media (max-width: 1000px) {
    max-width: 700px;
    padding: 45px;
    .header-border {
      padding: 80px;
    }
    h1 {
      font-size: 60px;
    }
    p {
      font-size: 24px;
    }
  }
  @media (max-width: 767px) {
    max-width: 500px;
    padding: 20px;
    margin-bottom: 20px;
    h1 {
      font-size: 44px;
    }
    p {
      font-size: 18px;
      line-height: 1.4;
    }
    .header-border {
      padding: 40px 20px;
    }
  }
`

export default Page

export const pageQuery = graphql`
  query PageById(
    # these variables are passed in via createPage.pageContext in gatsby-node.js
    $id: String!
  ) {
    bannerImg: file(relativePath: { eq: "header04.jpg" }) {
      childImageSharp {
        gatsbyImageData(
          width: 2000
          placeholder: BLURRED
          formats: [AUTO, WEBP, AVIF]
        )
      }
    }
    # selecting the current page by id
    page: wpPage(id: { eq: $id }) {
      id
      title
      SEOmeta {
        metaTitle
        metaDescription
        metaKeywords
        ogTitle
        ogDescription
        ogImage {
          localFile {
            childImageSharp {
              fluid(maxWidth: 1320, quality: 100) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
        twitterTitle
        twitterDescription
        twitterImage {
          localFile {
            childImageSharp {
              fluid(maxWidth: 1320, quality: 100) {
                ...GatsbyImageSharpFluid_withWebp
              }
            }
          }
        }
      }
      PageBuilderSubtitle {
        pbPageSubtitle
      }
      PageBuilder {
        standardPageBuilder {
          ... on WpPage_Pagebuilder_StandardPageBuilder_PbOneColumnContent {
            fieldGroupName
            pbFullContent
          }
          ... on WpPage_Pagebuilder_StandardPageBuilder_PbTwoHalfColumns {
            fieldGroupName
            pbLeftColumnContent
            pbRightColumnContent
          }
          ... on WpPage_Pagebuilder_StandardPageBuilder_PbTwoThirdsOneThird {
            fieldGroupName
            pbLeftColumnTwoThirds
            pbRightColumnOneThird
          }
          ... on WpPage_Pagebuilder_StandardPageBuilder_PbTwoColumnRightImage {
            fieldGroupName
            pbImageLeftColumnContent
            pbRightColumnImage {
              title
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 800
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                  )
                }
              }
            }
          }
          ... on WpPage_Pagebuilder_StandardPageBuilder_PbTwoColumnLeftImage {
            fieldGroupName
            pbImageRightColumnContent
            pbLeftColumnImage {
              title
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 800
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                  )
                }
              }
            }
          }
          ... on WpPage_Pagebuilder_StandardPageBuilder_PbPageTestimonial {
            fieldGroupName
            pbAuthorInfo
            pbTestimonialAuthor
            pbTestimonialQuote
          }
          ... on WpPage_Pagebuilder_StandardPageBuilder_PbCallToAction {
            fieldGroupName
            pbCtaButtonLink
            pbCtaButtonText
            pbCtaTopContent
          }
          ... on WpPage_Pagebuilder_StandardPageBuilder_PbFullWidthImage {
            fieldGroupName
            imageUpload {
              localFile {
                childImageSharp {
                  gatsbyImageData(
                    width: 1160
                    placeholder: BLURRED
                    formats: [AUTO, WEBP, AVIF]
                  )
                }
              }
            }
          }
        }
      }
    }
  }
`
